import { injectable } from 'inversify';
import type {
  IAuthRepository,
  LoginRequest,
  LoginResponse,
  Enable2FAResponse,
} from '@/domain/repositories/IAuthRepository';
import { User } from '@/domain/entities/User';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import { UserMapper } from './mappers/UserMapper';
import type { AuthDTO } from '@/application/dtos/auth/AuthDTO';
import { InvalidCredentialsError } from '@/domain/errors/InvalidCredentialsError';

@injectable()
export class ApiAuthRepository implements IAuthRepository {
  async login(request: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await httpClient.post<AuthDTO>('/auth/login', {
        username: request.username,
        password: request.password,
        twoFactorCode: request.twoFactorCode,
        twoFactorSecret: request.twoFactorSecret,
      });

      const data = response.data;

      let user: User | null = null;
      if (data.user) {
        user = UserMapper.toDomain(data.user);
      }

      return {
        token: data.token,
        user,
        requiresTwoFactor: data.requiresTwoFactor,
        requiresTwoFactorSetup: data.requiresTwoFactorSetup,
        twoFactorSecret: data.twoFactorSecret,
        twoFactorQrCodeUrl: data.twoFactorQrCodeUrl,
        expiresIn: data.expiresIn,
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new InvalidCredentialsError('Invalid username or password');
      }
      throw error;
    }
  }

  async enable2FA(): Promise<Enable2FAResponse> {
    try {
      const response = await httpClient.post<{ secret: string; qrCodeUrl: string }>('/auth/enable-2fa');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async verify2FASetup(code: string, secret: string): Promise<void> {
    try {
      await httpClient.post('/auth/verify-2fa-setup', { code, secret });
    } catch (error) {
      throw error;
    }
  }

  async disable2FA(password: string): Promise<void> {
    try {
      await httpClient.post('/auth/disable-2fa', { password });
    } catch (error) {
      throw error;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await httpClient.get<any>('/auth/me');
      const userData = response.data;
      
      if (!userData) {
        return null;
      }

      // Asegurar que tenga createdAt si no viene del backend
      if (!userData.createdAt) {
        userData.createdAt = new Date().toISOString();
      }

      return UserMapper.toDomain(userData);
    } catch (error: any) {
      // Si es un 401, lanzar el error para que se maneje arriba
      // Para otros errores, retornar null silenciosamente
      if (error.response?.status === 401) {
        throw error;
      }
      console.error('Error fetching current user:', error);
      return null;
    }
  }

  async changeUserTenant(tenantId: number | string): Promise<void> {
    try {
      const response = await httpClient.post('/tenants/change-user-tenant', { tenantId });
      const data = response.data || {};

      const storedUser = localStorage.getItem('admin_user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          const resolvedTenantId = data.tenantId ?? data.currentTenantId ?? tenantId;
          const resolvedTenantName = data.tenantName ?? data.user?.tenant?.nombre ?? userData?.tenant?.nombre;
          const resolvedTenantEmail = data.user?.tenant?.emailAdministrador ?? userData?.tenant?.emailAdministrador;

          userData.tenant = {
            id: resolvedTenantId,
            nombre: resolvedTenantName,
            ...(resolvedTenantEmail ? { emailAdministrador: resolvedTenantEmail } : {}),
          };

          localStorage.setItem('admin_user', JSON.stringify(userData));
        } catch (storageError) {
          console.error('Error updating tenant in localStorage:', storageError);
        }
      }
    } catch (error) {
      throw error;
    }
  }
}
